import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams, useNavigate } from "react-router-dom";
import { UserContext } from "../App";
import { 
  CheckIcon, 
  ClipboardDocumentIcon, 
  TrashIcon, 
  ArrowPathIcon,
  XMarkIcon
} from '@heroicons/react/24/solid';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, DocumentTextIcon as DocumentTextOutline, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { db } from '../firebase';
import { isTapeReading } from '../utils/tradeHelpers';
import { collection, getDocs, query, orderBy, writeBatch, doc, deleteDoc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import Spinner from '../components/MatrixLoader';
import ZoomableImageModal from '../components/ZoomableImageModal';
import ExportFormatPicker from '../components/context/ExportFormatPicker';
import { resolveTradeEnvFields } from '../utils/tradeEnvFields';
import {
  buildAiContextPrompt,
  formatContextValue,
  getMindsetExecutionRows,
} from '../utils/journalContext';
import { getEntryDuration } from '../utils/tradeDuration';
import {
  entryToText,
  formatContextExport,
  getExportFormat,
  loadExportOptions,
  saveExportOptions,
} from '../utils/contextExport';
import { DATE_RANGE_PRESETS, getDateRangePreset, isRollingDatePreset } from '../utils/dateRangePresets';
import { Menu, useConfirm, useToast } from '../components/ui';

const TOOLBAR_BUTTON = 'flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-[#F3F4F6] px-2 text-sm font-medium text-[#3B82F6] transition-colors hover:bg-[#E5E7EB] dark:bg-[#374151] dark:text-[#60A5FA] dark:hover:bg-[#4B5563] sm:px-4';

function CopyStateIcon({ copied }) {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={copied ? 'check' : 'clipboard'}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0 }}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
      >
        {copied
          ? <CheckIcon className="theme-success w-3.5 h-3.5" />
          : <ClipboardDocumentIcon className="w-3.5 h-3.5 text-[#3B82F6] dark:text-[#60A5FA]" />}
      </motion.div>
    </AnimatePresence>
  );
}

const getClearTimestampKey =(userId, accountId) => `contextClearTimestamp_${userId}_${accountId}`;

// Normalize an entry's created time to milliseconds since epoch for reliable sorting
function getCreatedTimeMs(entry) {
  if (!entry) return 0;
  // Prefer the created field if present
  if (entry.created && typeof entry.created === 'string') {
    const createdStr = entry.created;
    // Handle format like: "2025-08-05T18:36:30.799Z-fj9ugj"
    const lastDashIndex = createdStr.lastIndexOf('-');
    const maybeIso = lastDashIndex > 10 ? createdStr.substring(0, lastDashIndex) : createdStr;
    const t = new Date(maybeIso).getTime();
    if (!Number.isNaN(t)) return t;
  }
  // Fallback to year/month/day fields if available
  if (entry.year && entry.month && entry.day) {
    const yearNum = parseInt(entry.year, 10);
    const monthNum = parseInt(entry.month, 10) - 1; // 0-indexed
    const dayNum = parseInt(entry.day, 10);
    const t = new Date(yearNum, monthNum, dayNum).getTime();
    if (!Number.isNaN(t)) return t;
  }
  return 0;
}

function parseLocalDateInput(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return null;
  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const ContextPage = ({ previewMode = false }) => {
  const { currentUser, accounts, selectedAccount } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const entryIdRef = useRef(null);
  const [exportOptions, setExportOptions] = useState(loadExportOptions);
  const [copied, setCopied] = useState(false);
  const [mostRecentCopied, setMostRecentCopied] = useState(false);
  const [copiedEntryId, setCopiedEntryId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(!previewMode);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);
  const [clearTimestamp, setClearTimestamp] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const hasAutoExpandedRef = useRef(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTicker, setFilterTicker] = useState("ALL");
  const [tickerSuggestions, setTickerSuggestions] = useState([]);
  const [showTickerSuggestions, setShowTickerSuggestions] = useState(false);
  const [filterWinLoss, setFilterWinLoss] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterAccount, setFilterAccount] = useState('all');
  // Quick range chip that set the dates (null once the dates are edited by hand).
  const [datePreset, setDatePreset] = useState(null);
  // Saved filters must load before any save runs, or the defaults could overwrite them.
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();
  // Store full field definitions instead of just labels
  const [fieldDefinitions, setFieldDefinitions] = useState({ tradeEnv: [] });

  // Load filter preferences
  useEffect(() => {
    if (!currentUser) return;
    
    const loadFilters = async () => {
      try {
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        const prefsSnap = await getDoc(prefsRef);
        
        if (prefsSnap.exists()) {
          const data = prefsSnap.data();
          if (data.contextFilters) {
            if (data.contextFilters.ticker !== undefined) {
              setFilterTicker(data.contextFilters.ticker);
            }
            if (data.contextFilters.winLoss !== undefined) {
              setFilterWinLoss(data.contextFilters.winLoss);
            }
            if (data.contextFilters.dateFrom !== undefined) {
              setFilterDateFrom(data.contextFilters.dateFrom);
            }
            if (data.contextFilters.dateTo !== undefined) {
              setFilterDateTo(data.contextFilters.dateTo);
            }
            if (data.contextFilters.account !== undefined) {
              setFilterAccount(data.contextFilters.account);
            }
            const savedPreset = data.contextFilters.datePreset;
            if (isRollingDatePreset(savedPreset)) {
              const range = getDateRangePreset(savedPreset);
              setFilterDateFrom(range.from);
              setFilterDateTo(range.to);
              setDatePreset(savedPreset);
            }
          }
        }
        setFiltersLoaded(true);
      } catch (error) {
        // Leave saving off so the defaults can't overwrite filters we failed to read.
        console.error('Error loading context filters:', error?.message);
      }
    };
    
    loadFilters();
  }, [currentUser]);

  // Save filter preferences when they change
  useEffect(() => {
    if (!currentUser || !filtersLoaded) return;
    
    const saveFilters = async () => {
      try {
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        await setDoc(prefsRef, {
          contextFilters: {
            ticker: filterTicker,
            winLoss: filterWinLoss,
            dateFrom: filterDateFrom,
            dateTo: filterDateTo,
            account: filterAccount,
            datePreset: isRollingDatePreset(datePreset) ? datePreset : null,
          }
        }, { merge: true });
      } catch (error) {
        console.error('Error saving context filters:', error);
      }
    };
    
    // Debounce saves to avoid too many writes
    const timeoutId = setTimeout(saveFilters, 500);
    return () => clearTimeout(timeoutId);
  }, [currentUser, filtersLoaded, filterTicker, filterWinLoss, filterDateFrom, filterDateTo, filterAccount, datePreset]);

  useEffect(() => {
    saveExportOptions(exportOptions);
  }, [exportOptions]);

  // Load custom field definitions with real-time updates
  useEffect(() => {
    if (!currentUser) return;
    
    const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
    
    // Set up real-time listener for settings changes
    const unsubscribe = onSnapshot(prefsRef, (prefsSnap) => {
      try {
        if (prefsSnap.exists()) {
          const data = prefsSnap.data();
          setFieldDefinitions({ tradeEnv: resolveTradeEnvFields(data) });
        } else {
          setFieldDefinitions({ tradeEnv: resolveTradeEnvFields({}) });
        }
      } catch (error) {
        console.error('Error processing settings:', error);
      }
    }, (error) => {
      console.error('Error listening to settings:', error);
    });
    
    // Cleanup: unsubscribe when component unmounts or currentUser changes
    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    
    // Check if there's a clear timestamp for all accounts
    const timestampKey = getClearTimestampKey(currentUser.uid, 'all');
    const savedTimestamp = sessionStorage.getItem(timestampKey);
    setClearTimestamp(savedTimestamp ? parseInt(savedTimestamp) : null);

    // Load persistent clear timestamp from Firestore (Context-only setting)
    (async () => {
      try {
        const ctxRef = doc(db, 'users', currentUser.uid, 'preferences', 'context');
        const snap = await getDoc(ctxRef);
        if (snap.exists()) {
          const ts = snap.data()?.clearTimestamp;
          if (typeof ts === 'number' && !Number.isNaN(ts)) {
            setClearTimestamp(ts);
            // Mirror to session for fast subsequent loads in session
            sessionStorage.setItem(timestampKey, String(ts));
          }
        }
      } catch (e) {
        console.warn('ContextPage: failed to load clear timestamp from Firestore', e);
      }
    })();
    
    // Always fetch entries from database
    const fetchEntries = async () => {
      setLoading(true);
      // Using static imports from the top of the file
      
      // Fetch entries from ALL accounts (not just selected account)
      let allEntries = [];
      
      // Get all accounts for the current user
      const accountsCol = collection(db, 'users', currentUser.uid, 'accounts');
      const accountsSnap = await getDocs(accountsCol);
      const accounts = accountsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Fetch entries from each account
      for (const account of accounts) {
        const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', account.id, 'entries');
        const q = query(entriesCol, orderBy('created', 'desc'));
        // Force server fetch
        const snap = await getDocs(q, { source: 'server' });
        const accountEntries = snap.docs.map(d => ({ 
          id: d.id,
          ...d.data(), 
          accountId: account.id,
          accountName: account.name
        }));
        allEntries.push(...accountEntries);
      }
      
      // Fetch orphaned entries (from deleted accounts)
      try {
        const orphanedCol = collection(db, 'users', currentUser.uid, 'orphanedEntries');
        const orphanedSnap = await getDocs(orphanedCol);
        const orphanedEntries = orphanedSnap.docs.map(d => ({ 
          id: d.id,
          ...d.data(), 
          accountId: d.data().originalAccountId || 'deleted',
          accountName: d.data().originalAccountName || 'Deleted Account',
          isOrphaned: true
        }));
        allEntries.push(...orphanedEntries);
      } catch (error) {
      }
      
      // Sort all entries by creation date (newest first) using normalized timestamp
      allEntries.sort((a, b) => getCreatedTimeMs(b) - getCreatedTimeMs(a));
      
      setEntries(allEntries);
      setLoading(false);
    };
    fetchEntries();
  }, [currentUser]); // Remove selectedAccount dependency since we're fetching from all accounts

  // Handle URL parameter to auto-expand and scroll to entry
  useEffect(() => {
    const entryId = searchParams.get('entryId');
    if (entryId && entries.length > 0 && !loading) {
      // Expand the card
      setExpandedCards(prev => {
        const newSet = new Set(prev);
        newSet.add(entryId);
        return newSet;
      });
      
      // Scroll to the card after a brief delay to ensure it's rendered
      setTimeout(() => {
        if (entryIdRef.current) {
          entryIdRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Clear the URL parameter after scrolling
          searchParams.delete('entryId');
          setSearchParams(searchParams, { replace: true });
        }
      }, 300);
    }
  }, [entries, loading, searchParams, setSearchParams]);

  const sortedEntries = useMemo(() => {
    let filteredEntries = [...entries];
    
    // If there's a clear timestamp, only show entries created after it
    if (clearTimestamp) {
      filteredEntries = entries.filter(entry => {
        if (!entry.created) {
          return false;
        }
        // Parse the timestamp from the created field (remove random suffix)
        // Format is like: "2025-08-05T18:36:30.799Z-fj9ugj"
        // Split by the last dash to remove the random suffix
        const lastDashIndex = entry.created.lastIndexOf('-');
        const cleanTimestamp = lastDashIndex > 10 ? entry.created.substring(0, lastDashIndex) : entry.created;
        const entryTime = new Date(cleanTimestamp).getTime();
        const isAfterClear = entryTime > clearTimestamp;
        return isAfterClear;
      });
    }
    
    const arr = filteredEntries.sort((a, b) => getCreatedTimeMs(b) - getCreatedTimeMs(a));
    return arr;
  }, [entries, clearTimestamp]);

  const filteredEntries = useMemo(() => {
    let filtered = [...sortedEntries];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry => {
        const text = entryToText(entry, fieldDefinitions).toLowerCase();
        return text.includes(query);
      });
    }
    
    // Ticker filter
    if (filterTicker && filterTicker !== 'ALL') {
      filtered = filtered.filter(entry => 
        entry.tickerTraded && entry.tickerTraded.toUpperCase() === filterTicker.toUpperCase()
      );
    }
    
    // Win/Loss filter
    if (filterWinLoss !== "all") {
      filtered = filtered.filter(entry => {
        if (entry.isDeposit || entry.isPayout || isTapeReading(entry) || entry.isResetExcluded) return false;
        const pnl = Number(entry.pnl) || 0;
        if (filterWinLoss === "win") return pnl > 0;
        if (filterWinLoss === "loss") return pnl < 0;
        return pnl === 0;
      });
    }
    
    // Date range filter
    if (filterDateFrom || filterDateTo) {
      filtered = filtered.filter(entry => {
        let entryDate = null;
        if (entry.month && entry.day) {
          const year = entry.year || new Date().getFullYear();
          const month = parseInt(entry.month) - 1;
          const day = parseInt(entry.day);
          entryDate = new Date(year, month, day);
        } else if (entry.created) {
          const lastDashIndex = entry.created.lastIndexOf('-');
          const cleanTimestamp = lastDashIndex > 10 ? entry.created.substring(0, lastDashIndex) : entry.created;
          entryDate = new Date(cleanTimestamp);
        }
        if (!entryDate || isNaN(entryDate.getTime())) return true;
        
        if (filterDateFrom) {
          const fromDate = parseLocalDateInput(filterDateFrom);
          if (fromDate && entryDate < fromDate) return false;
        }
        if (filterDateTo) {
          const toDate = parseLocalDateInput(filterDateTo);
          if (toDate) {
            toDate.setHours(23, 59, 59, 999);
            if (entryDate > toDate) return false;
          }
        }
        return true;
      });
    }
    
    // Account filter
    if (filterAccount && filterAccount !== 'all') {
      filtered = filtered.filter(entry => {
        if (filterAccount === 'current') {
          // Show only entries from the currently selected account
          if (!selectedAccount || !selectedAccount.id) {
            return false;
          }
          return entry.accountId === selectedAccount.id;
        } else {
          // Show entries from a specific account by ID
          return entry.accountId === filterAccount;
        }
      });
    }
    
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedEntries, searchQuery, filterTicker, filterWinLoss, filterDateFrom, filterDateTo, filterAccount, selectedAccount]);

  // Auto-expand the newest entry when entries are first loaded or when a new entry is added
  useEffect(() => {
    if (!loading && filteredEntries.length > 0) {
      const firstEntry = filteredEntries[0];
      const firstEntryId = firstEntry.id || `${firstEntry.created}-${firstEntry.tickerTraded}`;
      
      // Track the last auto-expanded entry ID
      if (!hasAutoExpandedRef.current || hasAutoExpandedRef.current !== firstEntryId) {
        // Only auto-expand if not already expanded
        if (!expandedCards.has(firstEntryId)) {
          setExpandedCards(prev => new Set([...prev, firstEntryId]));
          hasAutoExpandedRef.current = firstEntryId;
        }
      }
    }
  }, [loading, filteredEntries, expandedCards]);

  const getUniqueTickers = useMemo(() => {
    const tickers = new Set();
    sortedEntries.forEach(entry => {
      if (entry.tickerTraded && !entry.isDeposit && !entry.isPayout) {
        tickers.add(entry.tickerTraded);
      }
    });
    return Array.from(tickers).sort();
  }, [sortedEntries]);

  const toggleCard = (entryId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  // Clipboard write with the legacy execCommand fallback (some embedded browsers block the async API).
  const writeClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        return document.execCommand('copy');
      } catch (fallbackError) {
        console.error('Fallback copy also failed:', fallbackError);
        return false;
      } finally {
        document.body.removeChild(textArea);
      }
    }
  };

  const exportFormatOptions = useMemo(
    () => ({ format: exportOptions.format, groups: exportOptions.groups, fieldDefinitions }),
    [exportOptions, fieldDefinitions]
  );

  const handleCopyEntry = async (entry) => {
    const copiedOk = await writeClipboard(formatContextExport([entry], exportFormatOptions));
    if (!copiedOk) {
      toast.error('Could not copy the entry. Try again.');
      return;
    }
    const entryId = entry.id || `${entry.created}-${entry.tickerTraded}`;
    setCopiedEntryId(entryId);
    setTimeout(() => setCopiedEntryId(null), 1200);
  };

  const getEntryDate = (entry) => {
    if (entry.month && entry.day) {
      const year = entry.year || new Date().getFullYear();
      const month = parseInt(entry.month) - 1;
      const day = parseInt(entry.day);
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    if (entry.created) {
      const lastDashIndex = entry.created.lastIndexOf('-');
      const cleanTimestamp = lastDashIndex > 10 ? entry.created.substring(0, lastDashIndex) : entry.created;
      return new Date(cleanTimestamp);
    }
    return new Date();
  };

  // What "Copy to Clipboard" / "Download Context" export. Must match what's visible, so it's built from filteredEntries.
  const combinedText = useMemo(
    () => formatContextExport(filteredEntries || [], exportFormatOptions).trim(),
    [filteredEntries, exportFormatOptions]
  );
  const hasEntries = Boolean(filteredEntries && filteredEntries.length > 0);

  const handleCopy = async () => {
    if (!combinedText) return;
    if (await writeClipboard(combinedText)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } else {
      toast.error('Could not copy to the clipboard. Try again.');
    }
  };

  const handleDownload = () => {
    if (!combinedText) return;
    const { extension, mime } = getExportFormat(exportOptions.format);
    const url = URL.createObjectURL(new Blob([combinedText], { type: `${mime};charset=utf-8` }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `bandz-ai-context-${new Date().toISOString().slice(0, 10)}.${extension}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const handleCopyMostRecent = async () => {
    // First entry is the most recent (sorted newest first, then filtered).
    const mostRecentEntry = filteredEntries?.[0];
    if (!mostRecentEntry) return;
    if (await writeClipboard(formatContextExport([mostRecentEntry], exportFormatOptions))) {
      setMostRecentCopied(true);
      setTimeout(() => setMostRecentCopied(false), 1200);
    } else {
      toast.error('Could not copy to the clipboard. Try again.');
    }
  };

  const applyDatePreset = (presetId) => {
    const range = getDateRangePreset(presetId);
    if (!range) return;
    setFilterDateFrom(range.from);
    setFilterDateTo(range.to);
    setDatePreset(presetId === 'all' ? null : presetId);
  };
  const activeDatePreset = datePreset || (!filterDateFrom && !filterDateTo ? 'all' : null);

  const llmPrompt = useMemo(() => buildAiContextPrompt(fieldDefinitions), [fieldDefinitions]);

  const handleCopyPrompt = () => {
    try {
      if (!llmPrompt || llmPrompt.trim().length === 0) {
        return;
      }

      // Copy to clipboard with error handling
      navigator.clipboard.writeText(llmPrompt).then(() => {
        setPromptCopied(true);
        setTimeout(() => setPromptCopied(false), 1200);
      }).catch((error) => {
        console.error('Failed to copy prompt to clipboard:', error);
        // Fallback: try to copy using document.execCommand
        const textArea = document.createElement('textarea');
        textArea.value = llmPrompt;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          setPromptCopied(true);
          setTimeout(() => setPromptCopied(false), 1200);
        } catch (fallbackError) {
          console.error('Fallback copy also failed:', fallbackError);
        }
        document.body.removeChild(textArea);
      });
    } catch (error) {
      console.error('Error in handleCopyPrompt:', error);
    }
  };

  // Manual refresh handler - fetches fresh data but respects clear timestamp
  const handleManualRefresh = async () => {
    if (!currentUser) return;
    setLoading(true);
    // Using static imports from the top of the file
    
    // Fetch entries from ALL accounts (not just selected account)
    let allEntries = [];
    
    // Get all accounts for the current user
    const accountsCol = collection(db, 'users', currentUser.uid, 'accounts');
    const accountsSnap = await getDocs(accountsCol);
    const accounts = accountsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Fetch entries from each account
    for (const account of accounts) {
      const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', account.id, 'entries');
      const q = query(entriesCol, orderBy('created', 'desc'));
      const snap = await getDocs(q, { source: 'server' });
      const accountEntries = snap.docs.map(d => ({ 
        id: d.id,
        ...d.data(), 
        accountId: account.id,
        accountName: account.name
      }));
      allEntries.push(...accountEntries);
    }
    
    // Fetch orphaned entries (from deleted accounts)
    try {
      const orphanedCol = collection(db, 'users', currentUser.uid, 'orphanedEntries');
      const orphanedSnap = await getDocs(orphanedCol);
      const orphanedEntries = orphanedSnap.docs.map(d => ({ 
        id: d.id,
        ...d.data(), 
        accountId: d.data().originalAccountId || 'deleted',
        accountName: d.data().originalAccountName || 'Deleted Account',
        isOrphaned: true
      }));
      allEntries.push(...orphanedEntries);
    } catch (error) {
    }
    
    // Sort all entries by creation date (newest first) using normalized timestamp
    allEntries.sort((a, b) => getCreatedTimeMs(b) - getCreatedTimeMs(a));
    
    setEntries(allEntries);
    setLoading(false);
  };


  // Delete individual entry (works for both orphaned and active account entries)
  const deleteEntry = async (entry) => {
    if (!currentUser || !entry || !entry.id) return;
    
    const confirmed = await confirm({
      tone: 'danger',
      title: 'Delete this entry?',
      description: "It is deleted permanently. This can't be undone.",
      confirmLabel: 'Delete entry',
    });
    if (!confirmed) return;

    try {
      let ref;
      // Check for orphaned entries (from deleted accounts)
      if (entry.isOrphaned || entry.accountId === 'deleted') {
        ref = doc(db, 'users', currentUser.uid, 'orphanedEntries', entry.id);
      } 
      // Standard entries from active accounts
      else if (entry.accountId) {
        ref = doc(db, 'users', currentUser.uid, 'accounts', entry.accountId, 'entries', entry.id);
      } else {
        console.error('Cannot delete entry: missing accountId or isOrphaned flag');
        return;
      }
      
      await deleteDoc(ref);
      
      // Remove from local state immediately
      setEntries(prev => prev.filter(e => e.id !== entry.id));
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Could not delete the entry. Try again.');
    }
  };

  // Permanently delete ONLY orphaned entries from Firestore (test data from deleted accounts)
  const handleDeleteShown = async () => {
    try {
      if (!currentUser) return;
      if (!sortedEntries || sortedEntries.length === 0) return;

      // Filter to only orphaned entries
      const orphanedEntries = sortedEntries.filter(entry => entry.isOrphaned || entry.accountId === 'deleted');
      
      if (orphanedEntries.length === 0) {
        toast.info('No entries from deleted accounts to remove.');
        return;
      }

      const confirmed = await confirm({
        tone: 'danger',
        title: `Delete ${orphanedEntries.length} entr${orphanedEntries.length === 1 ? 'y' : 'ies'} from deleted accounts?`,
        description: "They are deleted permanently. This can't be undone.",
        confirmLabel: 'Delete entries',
      });
      if (!confirmed) return;

      const batch = writeBatch(db);
      let deleteCount = 0;
      orphanedEntries.forEach(entry => {
        if (!entry.id) return;
        const ref = doc(db, 'users', currentUser.uid, 'orphanedEntries', entry.id);
        batch.delete(ref);
        deleteCount += 1;
      });

      if (deleteCount === 0) return;

      await batch.commit();

      // Remove deleted entries from local state immediately
      const deletedIds = new Set(orphanedEntries.map(e => e.id).filter(Boolean));
      setEntries(prev => prev.filter(e => !deletedIds.has(e.id)));
    } catch (error) {
      console.error('Error deleting orphaned entries:', error);
    }
  };


  return (
    <div className="page-shell animate-fadeIn">
      <div className="flex flex-col gap-6 w-full max-w-6xl mx-auto">
        <section className="flex flex-col gap-4 rounded-card border border-[#E5E7EB] bg-white p-4 shadow-subtle dark:border-[#374151] dark:bg-[#1F2937] sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-base font-semibold text-[#111827] dark:text-[#F9FAFB]">Import broker trades</h1>
            <p className="mt-1 text-sm text-[#6B7280] dark:text-[#9CA3AF]">Upload a CSV, preview every row, and skip duplicate entries.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/import-trades')}
            className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-lg bg-[#3B82F6] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#2563EB]"
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
            Import CSV
          </button>
        </section>
        
        {/* Action toolbar: the two copy actions stay visible, the rest live in "More". */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!hasEntries}
              className={`${TOOLBAR_BUTTON} ${hasEntries ? '' : 'cursor-not-allowed opacity-50'}`}
            >
              <CopyStateIcon copied={copied} />
              {copied ? "Copied!" : "Copy to Clipboard"}
            </button>
            <button
              type="button"
              onClick={handleCopyMostRecent}
              disabled={!hasEntries}
              className={`${TOOLBAR_BUTTON} ${hasEntries ? '' : 'cursor-not-allowed opacity-50'}`}
            >
              <CopyStateIcon copied={mostRecentCopied} />
              {mostRecentCopied ? "Copied!" : "Copy Most Recent"}
            </button>
          </div>

          <ExportFormatPicker options={exportOptions} onChange={setExportOptions} />

          <Menu
            label="More actions"
            align="start"
            items={[
              { label: 'Download Context', icon: ArrowDownTrayIcon, onClick: handleDownload, disabled: !hasEntries },
              { label: 'Refresh', icon: ArrowPathIcon, onClick: handleManualRefresh },
              { label: 'Analytics', icon: DocumentTextOutline, onClick: () => navigate('/visualize') },
              'divider',
              { label: 'Clear', icon: TrashIcon, onClick: handleDeleteShown, tone: 'danger' },
            ]}
            trigger={(triggerProps) => (
              <button
                {...triggerProps}
                type="button"
                title="More actions"
                className="flex h-11 min-w-[44px] items-center justify-center gap-1.5 rounded-lg bg-[#F3F4F6] px-3 text-sm font-medium text-[#3B82F6] transition-colors hover:bg-[#E5E7EB] dark:bg-[#374151] dark:text-[#60A5FA] dark:hover:bg-[#4B5563]"
              >
                <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
                <span className="hidden sm:inline">More</span>
              </button>
            )}
          />

          {/* Account Filter */}
          <select
            value={filterAccount}
            onChange={(e) => setFilterAccount(e.target.value)}
            aria-label="Account"
            className="h-11 min-w-0 flex-1 px-3 text-sm font-medium bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] border border-[#E5E7EB] dark:border-[#374151] rounded-lg focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none sm:ml-auto sm:max-w-[16rem] sm:flex-none"
          >
            <option value="all">All Accounts</option>
            {selectedAccount && (
              <option value="current">Current Account ({selectedAccount.name})</option>
            )}
            {accounts && accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => setShowHelpModal(true)}
            className="w-10 h-10 shrink-0 rounded-full text-[#3B82F6] dark:text-[#60A5FA] hover:text-[#2563EB] dark:hover:text-[#3B82F6] transition-colors duration-200 bg-[#F3F4F6] dark:bg-[#374151] hover:bg-[#E5E7EB] dark:hover:bg-[#4B5563] shadow-subtle border border-[#E5E7EB] dark:border-[#4B5563] flex items-center justify-center"
            title="How to use with AI"
            aria-label="How to use with AI"
          >
            <span className="text-2xl font-bold" aria-hidden="true">?</span>
          </button>
        </div>

        {/* Search and Filter Controls */}
        {!loading && sortedEntries.length > 0 && (
          <div className="bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card p-4 shadow-subtle">
            <div className="flex flex-col md:flex-row gap-4">
              <input
                type="text"
                placeholder="Search trades..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 h-11 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none transition-all"
              />
              <div className="relative">
                <input
                  type="text"
                  value={filterTicker}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setFilterTicker(value);
                    if (value && value !== 'ALL') {
                      const filtered = getUniqueTickers.filter(t => 
                        t.toUpperCase().startsWith(value.toUpperCase())
                      );
                      setTickerSuggestions(filtered);
                      setShowTickerSuggestions(filtered.length > 0);
                    } else {
                      setTickerSuggestions([]);
                      setShowTickerSuggestions(false);
                    }
                  }}
                  onFocus={() => {
                    if (filterTicker && filterTicker !== 'ALL') {
                      const filtered = getUniqueTickers.filter(t => 
                        t.toUpperCase().startsWith(filterTicker.toUpperCase())
                      );
                      setTickerSuggestions(filtered);
                      setShowTickerSuggestions(filtered.length > 0);
                    } else {
                      setTickerSuggestions(getUniqueTickers);
                      setShowTickerSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowTickerSuggestions(false), 200);
                  }}
                  placeholder="ALL"
                  className="bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 h-11 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none w-full"
                />
                {showTickerSuggestions && tickerSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card shadow-elevated z-50 max-h-40 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setFilterTicker('ALL');
                        setShowTickerSuggestions(false);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-[#F3F4F6] dark:hover:bg-[#374151] text-sm text-[#111827] dark:text-[#F9FAFB] font-medium"
                    >
                      ALL
                    </button>
                    {tickerSuggestions.map(ticker => (
                      <button
                        key={ticker}
                        type="button"
                        onClick={() => {
                          setFilterTicker(ticker);
                          setShowTickerSuggestions(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-[#F3F4F6] dark:hover:bg-[#374151] text-sm text-[#111827] dark:text-[#F9FAFB]"
                      >
                        {ticker}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <select
                value={filterWinLoss}
                onChange={(e) => setFilterWinLoss(e.target.value)}
                className="bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 h-11 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none"
              >
                <option value="all">All Trades</option>
                <option value="win">Wins Only</option>
                <option value="loss">Losses Only</option>
                <option value="breakeven">Breakeven</option>
              </select>
              <div className="grid grid-cols-2 gap-4 md:flex">
                <input
                  type="date"
                  aria-label="From date"
                  value={filterDateFrom}
                  onChange={(e) => { setFilterDateFrom(e.target.value); setDatePreset(null); }}
                  className="min-w-0 bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] px-2.5 py-3 sm:p-3 h-11 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 dark:[&::-webkit-calendar-picker-indicator]:invert"
                />
                <input
                  type="date"
                  aria-label="To date"
                  value={filterDateTo}
                  onChange={(e) => { setFilterDateTo(e.target.value); setDatePreset(null); }}
                  className="min-w-0 bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] px-2.5 py-3 sm:p-3 h-11 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#3B82F6] focus:border-[#3B82F6] focus:outline-none [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 dark:[&::-webkit-calendar-picker-indicator]:invert"
                />
              </div>
            </div>
            <div role="group" aria-label="Quick date range" className="mt-3 flex flex-wrap items-center gap-1 sm:gap-1.5 md:justify-end">
              {DATE_RANGE_PRESETS.map(({ id, label }) => {
                const active = activeDatePreset === id;
                return (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => applyDatePreset(id)}
                    className={`h-9 whitespace-nowrap rounded-full border px-1.5 text-xs font-medium transition-colors sm:h-8 sm:px-3 ${
                      active
                        ? 'border-[#3B82F6] bg-[#EFF6FF] text-[#2563EB] dark:border-[#3B82F6] dark:bg-[#1E3A8A]/40 dark:text-[#93C5FD]'
                        : 'border-[#E5E7EB] text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#111827] dark:border-[#374151] dark:text-[#9CA3AF] dark:hover:bg-[#374151] dark:hover:text-[#F9FAFB]'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content Area - Trade Cards */}
        <div className="flex-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <Spinner size={48} />
              <p className="text-[#6B7280] dark:text-[#9CA3AF] mt-4">Loading trading data...</p>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="flex items-center justify-center py-24">
              <div className="text-center text-[#6B7280] dark:text-[#9CA3AF]">
                <DocumentTextOutline className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No trading data available</p>
                <p className="text-sm">Add some trades to see your context here</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredEntries.map((entry) => {
                const entryId = entry.id || `${entry.created}-${entry.tickerTraded}`;
                const isExpanded = expandedCards.has(entryId);
                const entryDate = getEntryDate(entry);
                const pnl = Number(entry.pnl) || 0;
                const isTrade = !entry.isDeposit && !entry.isPayout && !entry.isBalanceAdjustment;
                const urlEntryId = searchParams.get('entryId');
                const shouldScroll = urlEntryId === entryId;
                
                return (
                  <motion.div
                    key={`${entry.accountId || ''}:${entryId}`}
                    ref={shouldScroll ? entryIdRef : null}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card shadow-subtle overflow-hidden"
                  >
                    {/* Header Row - Always Visible */}
                    <div 
                      className="p-2 flex items-center justify-between cursor-pointer hover:bg-[#F9FAFB] dark:hover:bg-[#374151] transition-colors"
                      onClick={() => toggleCard(entryId)}
                    >
                      <div className="flex min-w-0 items-center gap-3 flex-1">
                        <div className="min-w-0 truncate text-base sm:text-lg font-bold text-[#111827] dark:text-[#F9FAFB]" title={entry.title || undefined}>
                          {entry.title || entryDate.toLocaleDateString()}
                        </div>
                        {isTrade && entry.tickerTraded && (
                          <span className="shrink-0 bg-[#DBEAFE] dark:bg-[#1E3A8A] text-[#3730A3] dark:text-[#93C5FD] px-2 py-0.5 rounded-full text-xs font-medium">
                            {entry.tickerTraded}
                          </span>
                        )}
                        {isTrade && !isTapeReading(entry) && (
                          <span className={`number-kpi shrink-0 whitespace-nowrap ${pnl > 0 ? 'theme-success' : pnl < 0 ? 'theme-loss' : 'text-[#111827] dark:text-[#F9FAFB]'}`}>
                            {pnl > 0 ? '+' : ''}{pnl.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/edit-trade/${entryId}`);
                          }}
                          className="p-2 text-sm font-medium text-[#8B5CF6] dark:text-[#A78BFA] hover:bg-[#F3F4F6] dark:hover:bg-[#374151] rounded-card transition-colors"
                          title="Edit this entry"
                        >
                          edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyEntry(entry);
                          }}
                          className="p-2 hover:bg-[#EFF6FF] dark:hover:bg-[#1E3A8A] rounded-card transition-colors"
                          title="Copy this trade"
                        >
                          <AnimatePresence mode="wait">
                            {copiedEntryId === entryId ? (
                              <motion.div
                                key="check"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                              >
                                <CheckIcon className="theme-success w-5 h-5" />
                              </motion.div>
                            ) : (
                              <motion.div
                                key="clipboard"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                              >
                                <ClipboardDocumentIcon className="w-5 h-5 text-[#3B82F6] dark:text-[#60A5FA] hover:text-[#2563EB] dark:hover:text-[#3B82F6]" />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteEntry(entry);
                          }}
                          className="p-2 hover:bg-[#FEE2E2] dark:hover:bg-[#7F1D1D] rounded-card transition-colors"
                          title="Delete this entry"
                        >
                          <XMarkIcon className="theme-loss w-5 h-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedCards(prev => {
                              const newSet = new Set(prev);
                              if (isExpanded) {
                                newSet.delete(entryId);
                              } else {
                                newSet.add(entryId);
                              }
                              return newSet;
                            });
                          }}
                          className="p-2 hover:bg-[#F3F4F6] dark:hover:bg-[#374151] rounded-card transition-colors"
                          title={isExpanded ? "Collapse" : "Expand"}
                        >
                          <motion.div
                            animate={{ rotate: isExpanded ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <svg className="w-5 h-5 text-[#111827] dark:text-[#F9FAFB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </motion.div>
                        </button>
                      </div>
                    </div>
                    
                    {/* Expanded Content */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="p-6 border-t border-[#E5E7EB] dark:border-[#374151]">
                            {/* Date - Biggest and at the top */}
                            {(() => {
                              let dateStr = 'N/A';
                              if (entry.month && entry.day) {
                                const year = entry.year || new Date().getFullYear();
                                const month = parseInt(entry.month);
                                const day = parseInt(entry.day);
                                dateStr = `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
                              } else if (entry.created) {
                                try {
                                  const lastDashIndex = entry.created.lastIndexOf('-');
                                  const cleanTimestamp = lastDashIndex > 10 ? entry.created.substring(0, lastDashIndex) : entry.created;
                                  const date = new Date(cleanTimestamp);
                                  if (!isNaN(date.getTime())) {
                                    const month = date.getMonth() + 1;
                                    const day = date.getDate();
                                    const year = date.getFullYear();
                                    dateStr = `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
                                  }
                                } catch (e) {
                                  // Ignore errors
                                }
                              }
                              return (
                                <div className="pb-6 mb-6 border-b border-[#D1D5DB] dark:border-[#4B5563]">
                                  <div className="text-3xl font-bold text-[#111827] dark:text-[#F9FAFB]">{dateStr}</div>
                                </div>
                              );
                            })()}
                            
                            {/* Screenshots - At the top after date */}
                            {entry.screenshots && entry.screenshots.length > 0 && (
                              <div className="pb-6 mb-6 border-b border-[#D1D5DB] dark:border-[#4B5563]">
                                <h4 className="text-base font-semibold text-[#374151] dark:text-[#D1D5DB] mb-4">Screenshots</h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                  {entry.screenshots.map((screenshot, idx) => (
                                    <img
                                      key={idx}
                                      src={screenshot}
                                      alt={`Screenshot ${idx + 1}`}
                                      onClick={() => setExpandedImage(screenshot)}
                                      className="w-full h-40 object-cover rounded-card border border-[#E5E7EB] dark:border-[#374151] cursor-pointer hover:opacity-90 transition-opacity"
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Basic Trade Information and Additional Fields - Side by side */}
                            <div className="pb-6 mb-6 border-b border-[#D1D5DB] dark:border-[#4B5563]">
                              <div className="grid grid-cols-2 gap-6">
                                {/* Left Column - Basic Trade Information */}
                                <div className="grid grid-cols-2 gap-2 text-base">
                                  {entry.accountName && (
                                    <div className="min-h-[1.5rem] flex items-center gap-1"><span className="text-[#374151] dark:text-[#9CA3AF]">Account:</span> <span className="text-[#111827] dark:text-[#F9FAFB]">{entry.accountName}</span></div>
                                  )}
                                  <div className="min-h-[1.5rem] flex items-center gap-1"><span className="text-[#374151] dark:text-[#9CA3AF]">Title:</span> <span className="text-[#111827] dark:text-[#F9FAFB]">{entry.title || 'N/A'}</span></div>
                                  <div className="min-h-[1.5rem] flex items-center gap-1"><span className="text-[#374151] dark:text-[#9CA3AF]">Rating:</span> <span className={`font-bold ${entry.tradeRating === 'A' ? 'theme-success' : entry.tradeRating === 'B' ? 'text-[#3B82F6]' : entry.tradeRating === 'C' ? 'text-[#F59E0B]' : entry.tradeRating === 'D' ? 'theme-loss' : 'text-[#111827] dark:text-[#F9FAFB]'}`}>{entry.tradeRating || 'N/A'}</span></div>
                                  <div className="min-h-[1.5rem] flex items-center gap-1"><span className="text-[#374151] dark:text-[#9CA3AF]">Ticker:</span> <span className="text-[#111827] dark:text-[#F9FAFB]">{entry.tickerTraded || 'N/A'}</span></div>
                                  {entry.tradingStyle === 'swing' && entry.year && entry.month && entry.day && (
                                    <div className="min-h-[1.5rem] flex items-center gap-1"><span className="text-[#374151] dark:text-[#9CA3AF]">Log Date:</span> <span className="text-[#111827] dark:text-[#F9FAFB]">{entry.year}-{String(entry.month).padStart(2, '0')}-{String(entry.day).padStart(2, '0')}</span></div>
                                  )}
                                  {entry.tradingStyle === 'swing' && entry.entryYear && entry.entryMonth && entry.entryDay && (
                                    <div className="min-h-[1.5rem] flex items-center gap-1"><span className="text-[#374151] dark:text-[#9CA3AF]">Entry Date:</span> <span className="text-[#111827] dark:text-[#F9FAFB]">{entry.entryYear}-{String(entry.entryMonth).padStart(2, '0')}-{String(entry.entryDay).padStart(2, '0')}</span></div>
                                  )}
                                  <div className="min-h-[1.5rem] flex items-center gap-1"><span className="text-[#374151] dark:text-[#9CA3AF]">Entry Time:</span> <span className="text-[#111827] dark:text-[#F9FAFB]">{entry.entryTime || 'N/A'}</span></div>
                                  {entry.tradingStyle === 'swing' && entry.exitYear && entry.exitMonth && entry.exitDay && (
                                    <div className="min-h-[1.5rem] flex items-center gap-1"><span className="text-[#374151] dark:text-[#9CA3AF]">Exit Date:</span> <span className="text-[#111827] dark:text-[#F9FAFB]">{entry.exitYear}-{String(entry.exitMonth).padStart(2, '0')}-{String(entry.exitDay).padStart(2, '0')}</span></div>
                                  )}
                                  <div className="min-h-[1.5rem] flex items-center gap-1"><span className="text-[#374151] dark:text-[#9CA3AF]">Exit Time:</span> <span className="text-[#111827] dark:text-[#F9FAFB]">{entry.exitTime || 'N/A'}</span></div>
                                  <div className="min-h-[1.5rem] flex items-center gap-1"><span className="text-[#374151] dark:text-[#9CA3AF]">Duration:</span> <span className="text-[#111827] dark:text-[#F9FAFB]">{getEntryDuration(entry) || 'N/A'}</span></div>
                                  {!isTapeReading(entry) && (
                                    <>
                                      <div className="min-h-[1.5rem] flex items-center gap-1">
                                        <span className="text-[#374151] dark:text-[#9CA3AF]">P&L:</span> 
                                        <span className={`${pnl > 0 ? 'theme-success' : pnl < 0 ? 'theme-loss' : 'text-[#111827] dark:text-[#F9FAFB]'}`}>
                                          {' '}{entry.pnl !== undefined && entry.pnl !== '' ? entry.pnl : 'N/A'}
                                        </span>
                                      </div>
                                      <div className="min-h-[1.5rem] flex items-center gap-1"><span className="text-[#374151] dark:text-[#9CA3AF]">R:R:</span> <span className="text-[#111827] dark:text-[#F9FAFB]">{entry.rr !== undefined && entry.rr !== '' ? entry.rr : 'N/A'}</span></div>
                                    </>
                                  )}
                                  <div className="min-h-[1.5rem] flex items-center gap-1"><span className="text-[#374151] dark:text-[#9CA3AF]">Trade Session:</span> <span className="text-[#111827] dark:text-[#F9FAFB]">{entry.tradeSession || 'N/A'}</span></div>
                                  <div className="min-h-[1.5rem] flex items-center gap-1"><span className="text-[#374151] dark:text-[#9CA3AF]">Economic Release:</span> <span className="text-[#111827] dark:text-[#F9FAFB]">{entry.economicRelease || 'N/A'}</span></div>
                                  {(() => {
                                    // Calculate day of the week (same logic as entryToText)
                                    let dayOfWeek = 'N/A';
                                    if (entry.month && entry.day) {
                                      const year = entry.year || new Date().getFullYear();
                                      const month = parseInt(entry.month) - 1;
                                      const day = parseInt(entry.day);
                                      const date = new Date(year, month, day);
                                      if (!isNaN(date.getTime())) {
                                        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                        dayOfWeek = days[date.getDay()];
                                      }
                                    } else if (entry.created) {
                                      try {
                                        const lastDashIndex = entry.created.lastIndexOf('-');
                                        if (lastDashIndex > 10) {
                                          const isoString = entry.created.substring(0, lastDashIndex);
                                          const date = new Date(isoString);
                                          if (!isNaN(date.getTime())) {
                                            const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                            dayOfWeek = days[date.getDay()];
                                          }
                                        }
                                      } catch (e) {
                                        // Ignore errors
                                      }
                                    }
                                    return (
                                      <div key="dayOfWeek" className="col-span-2 min-h-[1.5rem] flex items-center gap-1">
                                        <span className="text-[#374151] dark:text-[#9CA3AF]">Day of the Week:</span> 
                                        <span className="text-[#111827] dark:text-[#F9FAFB]"> {dayOfWeek}</span>
                                      </div>
                                    );
                                  })()}
                                </div>
                                
                                {/* Right Column - Additional Fields and POI */}
                                {(() => {
                                  const definedFieldIds = new Set((fieldDefinitions.tradeEnv || []).map(f => f.id));
                                  const excludedKeys = ['title', 'tickerTraded', 'entryTime', 'exitTime', 'duration', 'pnl', 'rr', 
                                          'tradeSession', 'economicRelease', 'poi', 'pdArray', 'target', 'notes', 'accountName',
                                          'isDeposit', 'isPayout', 'isBalanceAdjustment', 'adjustmentTarget', 'isTapeReading', 'created', 'year', 'month', 'day',
                                          'entryYear', 'entryMonth', 'entryDay', 'exitYear', 'exitMonth', 'exitDay', 'tradingStyle',
                                          'screenshots', 'accountBalance', 'id', 'tapeReading', 'accountId', 'dayOfWeek', 'tradeRating',
                                          'mindsetExecution', 'isResetExcluded'];
                                  const customFields = Object.keys(entry).filter(key => 
                                    !excludedKeys.includes(key) && 
                                    !definedFieldIds.has(key) &&
                                    entry[key] !== undefined && 
                                    entry[key] !== null && 
                                    entry[key] !== ''
                                  );
                                  
                                  // Format values like entryToText does
                                  const formatFieldValue = (value) => {
                                    if (value === true || value === 'true') return 'Yes';
                                    if (value === false || value === 'false') return 'No';
                                    return formatContextValue(value);
                                  };
                                  
                                  // Check if PD Array exists
                                  const hasPdArray = entry.pdArray && typeof entry.pdArray === 'string' && entry.pdArray.trim() !== '';

                                  // Check if Target exists
                                  const hasTarget = entry.target && typeof entry.target === 'string' && entry.target.trim() !== '';
                                  
                                  // If no custom fields, no PD Array, and no Target, return null
                                  if (customFields.length === 0 && !hasPdArray && !hasTarget) return null;
                                  
                                  return (
                                    <div className="flex flex-col gap-3 text-base">
                                      {customFields.map(key => (
                                        <div key={key} className="min-h-[1.5rem] flex items-center gap-1">
                                          <span className="text-[#374151] dark:text-[#9CA3AF]">{key}:</span> 
                                          <span className="text-[#111827] dark:text-[#F9FAFB]"> {formatFieldValue(entry[key])}</span>
                                        </div>
                                      ))}
                                      {hasTarget && (
                                        <div className="min-h-[1.5rem] flex items-center gap-1">
                                          <span className="text-[#374151] dark:text-[#9CA3AF]">Target:</span>
                                          <span className="text-[#111827] dark:text-[#F9FAFB]"> {entry.target}</span>
                                        </div>
                                      )}
                                      {hasPdArray && (
                                        <div className="min-h-[1.5rem] flex items-center gap-1">
                                          <span className="text-[#374151] dark:text-[#9CA3AF]">What PD array did you enter off of:</span> 
                                          <span className="text-[#111827] dark:text-[#F9FAFB]"> {entry.pdArray}</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                            
                            {/* Trade Environment Fields - Show all enabled fields, matching entryToText logic */}
                            {fieldDefinitions.tradeEnv && fieldDefinitions.tradeEnv.filter(field => field.enabled).length > 0 && (
                              <div className="pb-6 mb-6 border-b border-[#D1D5DB] dark:border-[#4B5563]">
                                <h4 className="text-base font-semibold text-[#374151] dark:text-[#D1D5DB] mb-4">Trade Environment</h4>
                                <div className="flex flex-col gap-3 text-base">
                                  {fieldDefinitions.tradeEnv
                                    .filter(field => field.enabled)
                                    .map(field => {
                                      const value = entry[field.id];
                                      // Only show if value is defined (matching entryToText behavior)
                                      if (value === undefined) return null;
                                      
                                      let formattedValue;
                                      // Use same formatting logic as entryToText
                                      if (field.id === 'aboveBelow0000' || field.id === 'aboveBelow0830') {
                                        formattedValue = value === true || value === 'above' ? 'Aligned' : value === false || value === 'below' ? 'Not Aligned' : 'N/A';
                                      } else if (field.fieldType === 'text') {
                                        formattedValue = value || 'N/A';
                                      } else if (field.fieldType === 'dropdown') {
                                        formattedValue = value || 'N/A';
                                      } else {
                                        // yesNoNA or default
                                        formattedValue = value === true ? 'Yes' : value === false ? 'No' : 'N/A';
                                      }
                                      
                                      return (
                                        <div key={field.id} className="min-h-[1.5rem] flex items-center gap-1">
                                          <span className="text-[#374151] dark:text-[#9CA3AF]">{field.label}:</span> 
                                          <span className="text-[#111827] dark:text-[#F9FAFB]"> {formattedValue}</span>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            )}

                            {getMindsetExecutionRows(entry).length > 0 && (
                              <div className="mb-6 border-b border-[#D1D5DB] pb-6 dark:border-[#4B5563]">
                                <h4 className="mb-4 text-base font-semibold text-[#374151] dark:text-[#D1D5DB]">Mindset & Execution</h4>
                                <div className="grid gap-3 text-base md:grid-cols-2">
                                  {getMindsetExecutionRows(entry).map((row) => (
                                    <div key={row.key} className="min-h-[1.5rem] min-w-0">
                                      <span className="text-[#374151] dark:text-[#9CA3AF]">{row.label}:</span>
                                      <span className="break-words text-[#111827] dark:text-[#F9FAFB]"> {row.value}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Notes - Always show, matching entryToText */}
                            <div>
                              <h4 className="text-base font-semibold text-[#374151] dark:text-[#D1D5DB] mb-3">Notes</h4>
                              <p className="text-base text-[#111827] dark:text-[#F9FAFB] whitespace-pre-wrap">{entry.notes || 'N/A'}</p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Help Modal */}
      <AnimatePresence>
        {showHelpModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4 pt-24"
            onClick={() => setShowHelpModal(false)}
          >
            <div className="relative">
              {/* Fixed X button outside the modal */}
              <button
                onClick={() => setShowHelpModal(false)}
                className="absolute -top-4 -right-4 z-10 w-10 h-10 bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-full flex items-center justify-center shadow-elevated transition-colors duration-200"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>

              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card max-w-4xl w-full max-h-[85vh] overflow-y-auto shadow-elevated"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="flex items-center gap-3 p-6 border-b border-[#E5E7EB] dark:border-[#374151]">
                  <span className="text-4xl font-bold text-[#3B82F6]">?</span>
                  <h2 className="text-2xl font-semibold text-[#111827] dark:text-[#F9FAFB]">How to Use Export & AI Context</h2>
                </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-[#3B82F6]">What is Export & AI Context?</h3>
                  <p className="text-[#6B7280] dark:text-[#9CA3AF] leading-relaxed">
                    Export & AI Context turns your filtered journal records—including V3 reviews and custom Trade Environment fields—into structured text that an AI assistant can analyze.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-[#3B82F6]">How to Use with AI</h3>
                  <div className="space-y-3 text-[#6B7280] dark:text-[#9CA3AF]">
                    <p><strong className="text-[#111827] dark:text-[#F9FAFB]">1.</strong> Copy your trading data using the "Copy to Clipboard" button above</p>
                    <p><strong className="text-[#111827] dark:text-[#F9FAFB]">2.</strong> Open any AI assistant (ChatGPT, Claude, Gemini, Grok, etc.)</p>
                    <p><strong className="text-[#111827] dark:text-[#F9FAFB]">3.</strong> Paste the prompt below to set up the AI as your trading analyst</p>
                    <p><strong className="text-[#111827] dark:text-[#F9FAFB]">4.</strong> Paste your trading data and let the AI analyze your patterns and performance</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-[#3B82F6]">AI Trading Analyst Prompt</h3>
                  <p className="text-[#6B7280] dark:text-[#9CA3AF] text-sm">Copy this prompt and paste it into your AI assistant:</p>
                  
                  <div className="relative">
                    <textarea
                      className="w-full h-80 bg-[#F9FAFB] dark:bg-[#111827] border border-[#E5E7EB] dark:border-[#374151] rounded-card p-4 text-[#111827] dark:text-[#E5E7EB] text-sm font-mono leading-relaxed resize-none"
                      value={llmPrompt}
                      readOnly
                    />
                    <button
                      onClick={handleCopyPrompt}
                      className="absolute top-3 right-3 flex items-center gap-2 px-3 py-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-sm rounded-card transition-colors duration-200 shadow-subtle"
                    >
                      <AnimatePresence mode="wait">
                        {promptCopied ? (
                          <motion.div
                            key="check"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          >
                            <CheckIcon className="w-4 h-4 text-white" />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="clipboard"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          >
                            <ClipboardDocumentIcon className="w-4 h-4" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                      {promptCopied ? "Copied!" : "Copy Prompt"}
                    </button>
                  </div>
                </div>

                <div className="bg-[#EFF6FF] dark:bg-[#1E3A8A] border border-[#BFDBFE] dark:border-[#1E40AF] rounded-card p-4">
                  <h4 className="text-lg font-semibold text-[#3B82F6] dark:text-[#60A5FA] mb-2">💡 Pro Tip</h4>
                  <p className="text-[#6B7280] dark:text-[#9CA3AF] text-sm">
                    After setting up the AI with this prompt, you can regularly paste your updated trading data to get 
                    ongoing analysis of your performance, identify your best setups, and spot areas for improvement!
                  </p>
                </div>
              </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ZoomableImageModal
        isOpen={!!expandedImage}
        images={expandedImage ? [expandedImage] : []}
        index={0}
        onClose={() => setExpandedImage(null)}
      />
    </div>
  );
};

export default ContextPage; 
